import type { ReactNode } from 'react';
import LegalLayout from '@/components/feature/LegalLayout';
import LegalSection from '@/components/feature/LegalSection';

const P = ({ children }: { children: ReactNode }) => (
  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{children}</p>
);

const UL = ({ items }: { items: ReactNode[] }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <i className="ri-checkbox-blank-circle-fill text-[6px] text-primary-500 mt-2.5 shrink-0"></i>
        <span className="text-sm md:text-base text-foreground-700 leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const Mail = ({ address }: { address: string }) => (
  <a
    href={`mailto:${address}`}
    className="text-primary-600 underline hover:text-primary-700 cursor-pointer break-all"
  >
    {address}
  </a>
);

const Ext = ({ href, label }: { href: string; label?: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary-600 underline hover:text-primary-700 cursor-pointer break-all"
  >
    {label ?? href}
  </a>
);

export default function Datenschutz() {
  return (
    <LegalLayout
      title="Datenschutzerklärung"
      subtitle="Hier informiere ich Sie darüber, welche Daten beim Besuch dieser Website erhoben werden, zu welchen Zwecken sie verarbeitet werden und welche Rechte Ihnen zustehen. Stand: Oktober 2026."
    >
      <LegalSection index={1} title="Verantwortliche Stelle">
        <P>Verantwortlich für die Datenverarbeitung auf dieser Website ist:</P>
        <P>
          Stephanie Wolf
          <br />
          Am Feldbusch 50
          <br />
          45889 Gelsenkirchen
          <br />
          Deutschland
        </P>
        <P>
          Telefon: 0173/1971818
          <br />
          E-Mail: <Mail address="info@stephanie-wolf.com" />
        </P>
        <P>
          Website: <Ext href="https://www.stephanie-wolf.net" label="https://www.stephanie-wolf.net" />
        </P>
      </LegalSection>

      <LegalSection index={2} title="Allgemeine Hinweise zur Datenverarbeitung">
        <P>
          Der Schutz Ihrer personenbezogenen Daten ist mir wichtig. Ich verarbeite personenbezogene
          Daten ausschließlich im Rahmen der geltenden gesetzlichen Datenschutzvorschriften,
          insbesondere der Datenschutz-Grundverordnung (DSGVO) und des
          Telekommunikation-Digitale-Dienste-Datenschutz-Gesetzes (TDDDG).
        </P>
        <P>
          Personenbezogene Daten sind alle Informationen, mit denen Sie persönlich identifiziert
          werden können, beispielsweise Ihr Name, Ihre E-Mail-Adresse, Ihre Telefonnummer oder Ihre
          IP-Adresse.
        </P>
        <P>
          In dieser Datenschutzerklärung informiere ich Sie darüber, welche Daten beim Besuch dieser
          Website erhoben werden, zu welchen Zwecken diese verarbeitet werden und welche Rechte Ihnen
          zustehen.
        </P>
      </LegalSection>

      <LegalSection index={3} title="Hosting und Bereitstellung der Website">
        <P>Diese Website wird bei folgendem Anbieter gehostet:</P>
        <P>
          STRATO AG
          <br />
          Pascalstraße 10
          <br />
          10587 Berlin
          <br />
          Deutschland
        </P>
        <P>
          Beim Aufruf der Website verarbeitet der Hosting-Anbieter technisch erforderliche Daten,
          insbesondere IP-Adressen, Datum und Uhrzeit des Zugriffs, aufgerufene Seiten,
          Browserinformationen, Betriebssystem und gegebenenfalls die zuvor besuchte Website.
        </P>
        <P>
          Die Verarbeitung erfolgt zur technischen Bereitstellung der Website, zur Gewährleistung der
          Stabilität und Sicherheit sowie zur Abwehr von Missbrauch.
        </P>
        <P>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Das berechtigte Interesse besteht in einem
          sicheren und zuverlässigen Betrieb der Website.
        </P>
        <P>
          Soweit STRATO personenbezogene Daten in meinem Auftrag verarbeitet, erfolgt dies auf
          Grundlage einer Vereinbarung zur Auftragsverarbeitung gemäß Art. 28 DSGVO.
        </P>
      </LegalSection>

      <LegalSection index={4} title="Server-Logfiles">
        <P>
          Beim Besuch der Website können Informationen in sogenannten Server-Logfiles gespeichert
          werden. Diese Daten dienen der technischen Fehleranalyse, der Gewährleistung der
          Systemsicherheit und der Erkennung möglicher Angriffe.
        </P>
        <P>
          Eine Zusammenführung dieser Daten mit anderen Datenquellen findet grundsätzlich nicht statt,
          sofern dies nicht zur Aufklärung konkreter Sicherheitsvorfälle erforderlich ist.
        </P>
        <P>
          Die Logfiles werden gelöscht, sobald sie für die genannten Zwecke nicht mehr erforderlich
          sind und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
        </P>
      </LegalSection>

      <LegalSection index={5} title="Kontaktaufnahme per E-Mail oder Telefon">
        <P>
          Wenn Sie mich per E-Mail oder Telefon kontaktieren, verarbeite ich die von Ihnen
          mitgeteilten Daten, insbesondere Ihren Namen, Ihre Kontaktdaten und den Inhalt Ihrer
          Anfrage.
        </P>
        <P>
          Die Verarbeitung erfolgt zur Bearbeitung Ihrer Anfrage, zur Kommunikation mit Ihnen und
          gegebenenfalls zur Vorbereitung oder Durchführung eines Vertrags.
        </P>
        <P>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit einem Vertrag oder
          vorvertraglichen Maßnahmen zusammenhängt. In anderen Fällen erfolgt die Verarbeitung auf
          Grundlage meines berechtigten Interesses an der Bearbeitung eingehender Anfragen gemäß
          Art. 6 Abs. 1 lit. f DSGVO.
        </P>
        <P>
          Ein Kontaktformular steht auf dieser Website nicht zur Verfügung. Für die Vereinbarung eines
          Termins wird ausschließlich das unter Ziffer 6 beschriebene Buchungstool von Calendly
          eingesetzt.
        </P>
        <P>
          Die Daten werden gelöscht, sobald die Anfrage abschließend bearbeitet wurde und keine
          gesetzlichen Aufbewahrungspflichten oder sonstigen berechtigten Gründe für eine weitere
          Speicherung bestehen.
        </P>
      </LegalSection>

      <LegalSection index={6} title="Terminvereinbarung über Calendly">
        <P>Für die Online-Terminvereinbarung wird der Dienst Calendly verwendet.</P>
        <P>Anbieter ist:</P>
        <P>
          Calendly LLC
          <br />
          BB&amp;T Tower
          <br />
          271 17th St NW, Suite 1000
          <br />
          Atlanta, GA 30363
          <br />
          USA
        </P>
        <P>
          Das Calendly-Buchungstool ist als Widget direkt in diese Website eingebettet. Beim Aufruf
          der entsprechenden Seite wird das Widget geladen und es können dabei bereits Daten – insbesondere
          Ihre IP-Adresse sowie technische Browserinformationen – an Calendly übertragen werden.
          Soweit hierfür eine Einwilligung erforderlich ist, wird die Einbindung über den auf dieser
          Website eingesetzten Consent-Manager gesteuert.
        </P>
        <P>
          Wenn Sie über Calendly einen Termin vereinbaren, werden die von Ihnen eingegebenen Daten
          verarbeitet. Dazu können insbesondere Ihr Name, Ihre E-Mail-Adresse, Ihre Angaben zum
          gewünschten Termin sowie weitere freiwillige Informationen gehören.
        </P>
        <P>
          Die Daten werden verwendet, um Termine zu organisieren, zu bestätigen und gegebenenfalls
          Terminänderungen oder weitere Informationen zu übermitteln.
        </P>
        <P>
          Die Verarbeitung erfolgt zur Durchführung vorvertraglicher Maßnahmen oder zur Erfüllung
          eines Vertrags gemäß Art. 6 Abs. 1 lit. b DSGVO, soweit die Terminvereinbarung damit
          zusammenhängt. In anderen Fällen ist Art. 6 Abs. 1 lit. f DSGVO als Rechtsgrundlage
          möglich.
        </P>
        <P>
          Calendly verarbeitet Daten auch in den USA. Für Übermittlungen personenbezogener Daten in
          Drittländer müssen die Voraussetzungen der Art. 44 ff. DSGVO erfüllt sein. Calendly
          verweist hierfür insbesondere auf das EU-US Data Privacy Framework und
          Standardvertragsklauseln. Welche Grundlage im konkreten Fall greift, ist anhand des
          aktuellen Vertrags und des Verarbeitungsmodells zu prüfen.
        </P>
        <P>
          Weitere Informationen finden Sie in der Datenschutzerklärung von Calendly:{' '}
          <Ext href="https://calendly.com/legal/privacy-notice" label="https://calendly.com/legal/privacy-notice" />
        </P>
      </LegalSection>

      <LegalSection index={7} title="Cookie-Consent-Management mit CCM19">
        <P>
          Diese Website verwendet den Cookie-Consent-Manager CCM19, um Einwilligungen für
          einwilligungspflichtige Cookies und vergleichbare Technologien zu verwalten.
        </P>
        <P>
          Der Dienst ermöglicht es, Ihre Auswahl zu speichern und die Ausführung entsprechend
          konfigurierter Dienste zu steuern.
        </P>
        <P>
          Dabei können Informationen zu Ihrer Einwilligung, zum Zeitpunkt der Auswahl und zu den
          gewählten Einstellungen verarbeitet werden. Je nach Konfiguration können diese Informationen
          in einem Cookie oder im lokalen beziehungsweise sitzungsbezogenen Browserspeicher abgelegt
          werden.
        </P>
        <P>
          Die Verarbeitung dient der Dokumentation und Umsetzung Ihrer Einwilligungsentscheidungen
          sowie der Erfüllung datenschutzrechtlicher Pflichten.
        </P>
        <P>
          Rechtsgrundlage für das Speichern von Informationen auf Ihrem Endgerät beziehungsweise den
          Zugriff darauf ist § 25 TDDDG, soweit dessen Voraussetzungen erfüllt sind. Die anschließende
          Verarbeitung personenbezogener Daten richtet sich nach der jeweils einschlägigen
          Rechtsgrundlage der DSGVO.
        </P>
        <P>
          Sie können Ihre Einwilligungsentscheidungen jederzeit über die auf der Website angebotenen
          Datenschutzeinstellungen ändern oder widerrufen.
        </P>
        <P>
          Weitere Informationen zum Dienst:{' '}
          <Ext href="https://www.ccm19.de/" label="https://www.ccm19.de/" />
        </P>
      </LegalSection>

      <LegalSection index={8} title="Externe Schriftarten und Symbole">
        <P>
          Diese Website verwendet die Schriftarten „Fraunces“ und „Manrope“. Diese werden über den
          Dienst Google Fonts der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
          Irland, eingebunden.
        </P>
        <P>
          Beim Laden der Schriftarten stellt Ihr Browser eine Verbindung zu den Servern von Google
          her. Dabei werden insbesondere Ihre IP-Adresse, technische Browserinformationen und
          Informationen über den Seitenaufruf an Google übertragen. Es kann nicht ausgeschlossen
          werden, dass dabei Daten an Server in Drittländern, insbesondere in die USA, übertragen
          werden.
        </P>
        <P>
          Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. f DSGVO. Das berechtigte
          Interesse besteht in einer einheitlichen und ansprechenden Darstellung der Website. Soweit
          eine Einwilligung erforderlich ist, erfolgt die Einbindung erst nach Ihrer Einwilligung
          über den Consent-Manager.
        </P>
        <P>
          Darüber hinaus werden zur Darstellung von Symbolen die Icon-Bibliotheken „Font Awesome“ und
          „Remix Icon“ über das Content Delivery Network cdnjs (Cloudflare, Inc.) geladen. Auch dabei
          wird beim Aufruf der Website eine Verbindung zu den Servern des Anbieters hergestellt und es
          können technische Daten, insbesondere Ihre IP-Adresse, übertragen werden.
        </P>
        <P>
          Weitere Informationen finden Sie in der Datenschutzerklärung von Google:{' '}
          <Ext href="https://policies.google.com/privacy" label="https://policies.google.com/privacy" />
        </P>
      </LegalSection>

      <LegalSection index={9} title="Keine Nutzung von Analyse- und Trackingdiensten">
        <P>
          Nach den derzeit vorliegenden Angaben werden auf dieser Website keine Analyse- oder
          Trackingdienste wie Google Analytics eingesetzt.
        </P>
        <P>
          Sollten künftig entsprechende Dienste hinzukommen, wird diese Datenschutzerklärung vor
          deren Einsatz angepasst. Soweit erforderlich, werden die Dienste erst nach einer wirksamen
          Einwilligung aktiviert.
        </P>
      </LegalSection>

      <LegalSection index={10} title="Verlinkungen zu externen Websites und sozialen Netzwerken">
        <P>
          Diese Website kann Links zu externen Websites, sozialen Netzwerken oder anderen
          Onlineangeboten enthalten.
        </P>
        <P>
          Beim bloßen Aufruf dieser Website werden über einfache externe Links grundsätzlich nicht
          automatisch Daten an den jeweiligen Anbieter übertragen. Erst wenn Sie einen solchen Link
          anklicken, wird die externe Website aufgerufen.
        </P>
        <P>
          Ab diesem Zeitpunkt gelten die Datenschutzbestimmungen des jeweiligen Anbieters. Auf dessen
          Datenverarbeitung habe ich keinen Einfluss.
        </P>
      </LegalSection>

      <LegalSection index={11} title="Speicherdauer">
        <P>
          Personenbezogene Daten werden nur so lange gespeichert, wie dies für den jeweiligen
          Verarbeitungszweck erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
        </P>
        <P>
          Entfallen die jeweiligen Zwecke und bestehen keine gesetzlichen oder sonstigen berechtigten
          Gründe für eine weitere Speicherung, werden die Daten gelöscht oder ihre Verarbeitung wird
          entsprechend eingeschränkt.
        </P>
      </LegalSection>

      <LegalSection index={12} title="Ihre Rechte">
        <P>Sie haben im Rahmen der gesetzlichen Voraussetzungen folgende Rechte:</P>
        <UL
          items={[
            <>
              <strong className="font-semibold text-foreground-900">Auskunft</strong> über die
              Verarbeitung Ihrer personenbezogenen Daten gemäß Art. 15 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Berichtigung</strong> unrichtiger
              oder unvollständiger Daten gemäß Art. 16 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Löschung</strong> Ihrer Daten
              gemäß Art. 17 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Einschränkung der Verarbeitung</strong>{' '}
              gemäß Art. 18 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Datenübertragbarkeit</strong> gemäß
              Art. 20 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Widerspruch</strong> gegen
              bestimmte Verarbeitungen gemäß Art. 21 DSGVO.
            </>,
            <>
              <strong className="font-semibold text-foreground-900">Widerruf erteilter Einwilligungen</strong>{' '}
              mit Wirkung für die Zukunft gemäß Art. 7 Abs. 3 DSGVO.
            </>,
          ]}
        />
        <P>
          Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt davon unberührt.
        </P>
        <P>
          Sie haben außerdem das Recht, sich bei einer Datenschutzaufsichtsbehörde über die
          Verarbeitung Ihrer personenbezogenen Daten zu beschweren.
        </P>
      </LegalSection>

      <LegalSection index={13} title="Zuständige Datenschutzaufsichtsbehörde">
        <P>
          Sie können sich insbesondere an die für Nordrhein-Westfalen zuständige
          Datenschutzaufsichtsbehörde wenden:
        </P>
        <P>
          Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW)
          <br />
          Kavalleriestraße 2–4
          <br />
          40213 Düsseldorf
          <br />
          Deutschland
        </P>
        <P>
          Website: <Ext href="https://www.ldi.nrw.de/" label="https://www.ldi.nrw.de/" />
        </P>
      </LegalSection>

      <LegalSection index={14} title="Datensicherheit">
        <P>
          Ich treffe im Rahmen der technischen und organisatorischen Möglichkeiten geeignete
          Maßnahmen, um personenbezogene Daten vor Verlust, Manipulation, unberechtigtem Zugriff und
          sonstigen unzulässigen Verarbeitungen zu schützen.
        </P>
        <P>
          Die Website wird über eine verschlüsselte HTTPS-Verbindung bereitgestellt, sofern diese
          technisch korrekt eingerichtet und aktiv ist.
        </P>
      </LegalSection>

      <LegalSection index={15} title="Aktualität dieser Datenschutzerklärung">
        <P>
          Diese Datenschutzerklärung wird angepasst, wenn sich die technische Ausstattung der
          Website, die eingesetzten Dienste oder die rechtlichen Anforderungen ändern.
        </P>
        <P>
          <strong className="font-semibold text-foreground-900">Stand: Oktober 2026</strong>
        </P>
      </LegalSection>
    </LegalLayout>
  );
}